import { create } from "zustand";

/**
 * The Activity layout's project filter.
 *
 * Persisted separately from `uiStateStore` for the same reason
 * `sidebarViewFiltersStore` is: that module is already large, and a single
 * scalar preference does not justify importing sidebar concerns into the
 * global UI store.
 *
 * `null` means "All projects". A stored key that no longer names a live
 * project is discarded at read time by `resolveActivityScopeKey`, not here —
 * this store has no view of the project list.
 */
const PERSISTED_KEY = "forge.sidebar.activityScope.v1";

export function parsePersistedActivityScopeKey(serialized: string | null): string | null {
  if (serialized === null) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(serialized);
  } catch {
    return null;
  }
  if (typeof raw !== "object" || raw === null) return null;
  const scopeKey = (raw as Record<string, unknown>).scopeKey;
  return typeof scopeKey === "string" && scopeKey.length > 0 ? scopeKey : null;
}

function loadInitialScopeKey(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return parsePersistedActivityScopeKey(window.localStorage.getItem(PERSISTED_KEY));
  } catch {
    return null;
  }
}

function persistScopeKey(scopeKey: string | null): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PERSISTED_KEY, JSON.stringify({ scopeKey }));
  } catch {
    // Ignore quota/storage errors — a lost filter preference must never break
    // the sidebar.
  }
}

interface ActivitySidebarScopeStore {
  readonly scopeKey: string | null;
  readonly archivedShelfExpanded: boolean;
  readonly setScopeKey: (scopeKey: string | null) => void;
  readonly toggleArchivedShelf: () => void;
}

export const useActivitySidebarScopeStore = create<ActivitySidebarScopeStore>((set) => ({
  scopeKey: loadInitialScopeKey(),
  // Collapsed by default: the point of the Done shelf is that landed work
  // stops competing for space with work still in flight.
  archivedShelfExpanded: false,
  setScopeKey: (scopeKey) => {
    persistScopeKey(scopeKey);
    set({ scopeKey });
  },
  toggleArchivedShelf: () =>
    set((state) => ({ archivedShelfExpanded: !state.archivedShelfExpanded })),
}));
